import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Search, Flame, Clock, X, Edit3, Plus, Filter,
  LayoutGrid, List, Trash2, CheckCircle2, Upload,
  Eye, EyeOff, Loader2, Copy, Image as ImageIcon
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/menus.css';
import axios from 'axios';
import { baseurl, imgurl } from '../../baseurl';

/* ═══════════════════════════════════════
   RESTAURANT & CAFÉ CATEGORIES
═══════════════════════════════════════ */
const PRESET_CATEGORIES = [
  { id: 'starters',      name: 'Starters',          icon: '🥗' },
  { id: 'soups',         name: 'Soups',              icon: '🍲' },
  { id: 'salads',        name: 'Salads',             icon: '🥙' },
  { id: 'breads',        name: 'Breads & Rotis',     icon: '🫓' },
  { id: 'main_course',   name: 'Main Course',        icon: '🍛' },
  { id: 'rice_biryani',  name: 'Rice & Biryani',     icon: '🍚' },
  { id: 'noodles_pasta', name: 'Noodles & Pasta',    icon: '🍜' },
  { id: 'pizza',         name: 'Pizza',              icon: '🍕' },
  { id: 'burgers',       name: 'Burgers',            icon: '🍔' },
  { id: 'sandwiches',    name: 'Sandwiches & Wraps', icon: '🌯' },
  { id: 'seafood',       name: 'Seafood',            icon: '🦐' },
  { id: 'tandoor',       name: 'Tandoor Specials',   icon: '🔥' },
  { id: 'chinese',       name: 'Chinese',            icon: '🥢' },
  { id: 'continental',   name: 'Continental',        icon: '🍽️' },
  { id: 'south_indian',  name: 'South Indian',       icon: '🥘' },
  { id: 'north_indian',  name: 'North Indian',       icon: '🫕' },
  { id: 'street_food',   name: 'Street Food',        icon: '🌮' },
  { id: 'snacks',        name: 'Snacks & Sides',     icon: '🍟' },
  { id: 'desserts',      name: 'Desserts',           icon: '🍰' },
  { id: 'ice_cream',     name: 'Ice Cream',          icon: '🍦' },
  { id: 'sweets',        name: 'Indian Sweets',      icon: '🍮' },
  { id: 'beverages',     name: 'Beverages',          icon: '🥤' },
  { id: 'juices',        name: 'Juices & Smoothies', icon: '🧃' },
  { id: 'lassi_shakes',  name: 'Lassi & Shakes',     icon: '🥛' },
  { id: 'tea_coffee',    name: 'Tea & Coffee',       icon: '☕' },
  { id: 'mocktails',     name: 'Mocktails',          icon: '🍹' },
  { id: 'cocktails',     name: 'Cocktails',          icon: '🍸' },
  { id: 'wines_beer',    name: 'Wines & Beer',       icon: '🍷' },
  { id: 'breakfast',     name: 'Breakfast',          icon: '🍳' },
  { id: 'brunch',        name: 'Brunch',             icon: '🥞' },
  { id: 'bakery',        name: 'Bakery & Pastries',  icon: '🥐' },
  { id: 'combos',        name: 'Combos & Meals',     icon: '🎁' },
  { id: 'kids_menu',     name: "Kid's Menu",         icon: '🧒' },
  { id: 'seasonal',      name: 'Seasonal Specials',  icon: '🌟' },
];

const DRINK_IDS = ['beverages','juices','lassi_shakes','tea_coffee','mocktails','cocktails','wines_beer'];

const DEFAULT_CATEGORIES = [{ id: 'all', name: 'All', icon: '🍽️', isSystem: true }];

const SORT_OPTIONS = [
  { id: 'default',    label: 'Default'   },
  { id: 'price_asc',  label: 'Price ↑'  },
  { id: 'price_desc', label: 'Price ↓'  },
  { id: 'name_asc',   label: 'A → Z'    },
  { id: 'available',  label: 'Available' },
];

const BLANK_FORM = {
  name: '', category: '', description: '',
  price_full: '', price_half: '', has_half_plate: false,
  calories: '', prep_time: '', tags: [],
  is_available: true,
  // image handling
  image_url: '',        // existing server URL (when editing)
  image_file: null,     // File object (new upload)
  image_preview: '',    // local blob URL for preview
};

/* ═══════════════════════════════════════
   AXIOS HELPER
═══════════════════════════════════════ */
const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({
    baseURL: `${baseurl}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

/* ═══════════════════════════════════════
   TOAST HOOK
═══════════════════════════════════════ */
function useToast() {
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
  const fire = useCallback((msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 2800);
  }, []);
  return { toast, fire };
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
function Menus() {
  const [products,     setProducts]     = useState([]);
  const [categories,   setCategories]   = useState(DEFAULT_CATEGORIES);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [activeCat,    setActiveCat]    = useState('all');
  const [search,       setSearch]       = useState('');
  const [sortBy,       setSortBy]       = useState('default');
  const [viewMode,     setViewMode]     = useState('grid');

  const [panel,        setPanel]        = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [panelTab,     setPanelTab]     = useState('info');

  const [form,         setForm]         = useState(BLANK_FORM);
  const [tagInput,     setTagInput]     = useState('');

  const [showCatModal, setShowCatModal] = useState(false);
  const [newCatName,   setNewCatName]   = useState('');
  const [newCatIcon,   setNewCatIcon]   = useState('🍴');

  const { toast, fire } = useToast();
  const tagInputRef  = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  /* ── Load ── */
  const loadData = async () => {
    setLoading(true);
    try {
      const [menuRes, catRes] = await Promise.all([
        api().get('/items').catch(() => ({ data: { items: [] } })),
        api().get('/categories').catch(() => ({ data: { categories: [] } })),
      ]);

      const items = menuRes.data.items || menuRes.data.products || [];
      setProducts(items.map(p => ({
        ...p,
        tags:           Array.isArray(p.tags) ? p.tags : (p.tags ? JSON.parse(p.tags) : []),
        is_available:   p.is_available !== undefined ? Boolean(p.is_available) : true,
        has_half_plate: Boolean(p.has_half_plate),
        price_full:     p.price_full ?? p.price ?? 0,
        price_half:     p.price_half ?? null,
      })));

      const apiCats = catRes.data.categories || [];
      setCategories([
        DEFAULT_CATEGORIES[0],
        ...apiCats.map(c => ({ id: String(c.id || c.name), name: c.name, icon: c.icon || '🍴' })),
      ]);
    } catch (err) {
      console.error('Load error:', err);
      fire('Failed to load menu', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Filter + Sort ── */
  const filtered = useMemo(() => {
    let list = products.filter(p =>
      (activeCat === 'all' || p.category === activeCat) &&
      p.name.toLowerCase().includes(search.toLowerCase())
    );
    switch (sortBy) {
      case 'price_asc':  list = [...list].sort((a, b) => a.price_full - b.price_full); break;
      case 'price_desc': list = [...list].sort((a, b) => b.price_full - a.price_full); break;
      case 'name_asc':   list = [...list].sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'available':  list = [...list].sort((a, b) => (b.is_available ? 1 : 0) - (a.is_available ? 1 : 0)); break;
      default: break;
    }
    return list;
  }, [products, activeCat, search, sortBy]);

  const stats = useMemo(() => ({
    total:       products.length,
    available:   products.filter(p => p.is_available).length,
    unavailable: products.filter(p => !p.is_available).length,
  }), [products]);

  /* ── Panel helpers ── */
  const openCreate = () => {
    setForm({ ...BLANK_FORM });
    setSelectedItem(null);
    setPanel('create');
    setPanelTab('info');
  };

  const openEdit = (item) => {
    setForm({
      name:           item.name           || '',
      category:       item.category       || '',
      description:    item.description    || item.desc || '',
      price_full:     item.price_full     ?? item.price ?? '',
      price_half:     item.price_half     ?? '',
      has_half_plate: Boolean(item.has_half_plate),
      calories:       item.calories       || '',
      prep_time:      item.prep_time      || item.prep || '',
      tags:           Array.isArray(item.tags) ? [...item.tags] : [],
      is_available:   item.is_available   !== false,
      image_url:      item.image_url      || item.img || '',
      image_file:     null,
      image_preview:  item.image_url      || item.img || '',
    });
    setSelectedItem(item);
    setPanel('edit');
    setPanelTab('info');
  };

  const closePanel = () => {
    // Revoke any blob URL to free memory
    if (form.image_preview?.startsWith('blob:')) {
      URL.revokeObjectURL(form.image_preview);
    }
    setPanel(null);
    setSelectedItem(null);
    setUploadProgress(0);
  };

  /* ── Form helpers ── */
  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  /* ── Image file selection ── */
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('image/')) {
      fire('Please select a valid image file', 'error');
      return;
    }
    // Validate size (5 MB max)
    if (file.size > 5 * 1024 * 1024) {
      fire('Image must be under 5 MB', 'error');
      return;
    }

    // Revoke old blob URL
    if (form.image_preview?.startsWith('blob:')) {
      URL.revokeObjectURL(form.image_preview);
    }

    const preview = URL.createObjectURL(file);
    setForm(f => ({
      ...f,
      image_file:    file,
      image_preview: preview,
      image_url:     '',   // clear old server URL; new file takes priority
    }));
  };

  const handleImageUrlChange = (url) => {
    // If user pastes a URL, clear any selected file
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview);
    setForm(f => ({
      ...f,
      image_url:     url,
      image_file:    null,
      image_preview: url,
    }));
  };

  const clearImage = () => {
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview);
    setForm(f => ({ ...f, image_url: '', image_file: null, image_preview: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addTag = (val) => {
    const v = val.trim();
    if (v && !form.tags.includes(v)) setF('tags', [...form.tags, v]);
    setTagInput('');
  };
  const removeTag = (t) => setF('tags', form.tags.filter(x => x !== t));

  /* ══════════════════════════════════════════════════════
     SAVE  —  uses multipart/form-data when a file is chosen
     otherwise falls back to JSON
  ══════════════════════════════════════════════════════ */
  const handleSave = async () => {
    if (!form.name.trim() || !form.price_full) {
      fire('Name and full plate price are required', 'error');
      return;
    }
    setSaving(true);
    setUploadProgress(0);

    try {
      let finalImageUrl = form.image_url; // will be replaced by server path if file uploaded

      /* ── Step 1: upload image file first (if a new file was picked) ── */
      if (form.image_file) {
        const imgData = new FormData();
        imgData.append('menu_image', form.image_file);   // field name the backend expects

        const uploadRes = await api().post('/items/upload-image', imgData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(pct);
          },
        });

        finalImageUrl = uploadRes.data.image_url;  // server returns stored path
      }

      /* ── Step 2: save item JSON ── */
      const payload = {
        name:           form.name.trim(),
        category:       form.category,
        description:    form.description,
        price_full:     parseFloat(form.price_full),
        price_half:     form.has_half_plate && form.price_half ? parseFloat(form.price_half) : null,
        has_half_plate: form.has_half_plate ? 1 : 0,
        calories:       form.calories    || null,
        prep_time:      form.prep_time   || null,
        tags:           JSON.stringify(form.tags),
        is_available:   form.is_available ? 1 : 0,
        image_url:      finalImageUrl,
      };

      if (panel === 'create') {
        const res = await api().post('/items', payload);
        const newItem = res.data.item || {
          ...payload, id: Date.now(),
          tags: form.tags, has_half_plate: form.has_half_plate,
        };
        setProducts(prev => [newItem, ...prev]);
        fire(`"${form.name}" added to menu`);
      } else {
        await api().put(`/items/${selectedItem.id}`, payload);
        setProducts(prev => prev.map(p =>
          p.id === selectedItem.id
            ? { ...p, ...payload, tags: form.tags, is_available: form.is_available, has_half_plate: form.has_half_plate }
            : p
        ));
        fire(`"${form.name}" updated`);
        loadData();
      }
      closePanel();
    } catch (err) {
      fire(err.response?.data?.message || 'Failed to save item', 'error');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api().delete(`/items/${item.id}`);
      setProducts(prev => prev.filter(p => p.id !== item.id));
      if (selectedItem?.id === item.id) closePanel();
      fire(`"${item.name}" deleted`);
    } catch { fire('Failed to delete item', 'error'); }
  };

  /* ── Toggle availability ── */
  const handleToggleAvail = async (item, e) => {
    e.stopPropagation();
    const next = !item.is_available;
    try {
      await api().put(`/items/${item.id}`, { is_available: next ? 1 : 0 });
      setProducts(prev => prev.map(p => p.id === item.id ? { ...p, is_available: next } : p));
      fire(next ? `"${item.name}" marked available` : `"${item.name}" marked unavailable`);
    } catch { fire('Failed to update availability', 'error'); }
  };

  /* ── Duplicate ── */
  const handleDuplicate = (item) => {
    setForm({
      name:           item.name + ' (Copy)',
      category:       item.category    || '',
      description:    item.description || item.desc || '',
      price_full:     item.price_full  ?? item.price ?? '',
      price_half:     item.price_half  ?? '',
      has_half_plate: Boolean(item.has_half_plate),
      calories:       item.calories    || '',
      prep_time:      item.prep_time   || item.prep || '',
      tags:           [...(item.tags   || [])],
      is_available:   true,
      image_url:      item.image_url   || item.img || '',
      image_file:     null,
      image_preview:  item.image_url   || item.img || '',
    });
    setSelectedItem(null);
    setPanel('create');
  };

  /* ── Add category ── */
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await api().post('/categories', { name: newCatName.trim(), icon: newCatIcon });
      const newCat = res.data.category || { id: newCatName.toLowerCase(), name: newCatName.trim(), icon: newCatIcon };
      setCategories(prev => [...prev, { id: String(newCat.id || newCat.name), name: newCat.name, icon: newCat.icon }]);
      setShowCatModal(false);
      setNewCatName('');
      fire(`Category "${newCat.name}" created`);
    } catch { fire('Failed to create category', 'error'); }
  };

  if (loading) {
    return (
      <div className="mn-root">
        <Navbar />
        <div className="mn-loading">
          <Loader2 size={36} className="mn-spin" />
          <p style={{ fontWeight: 600, color: 'var(--muted)' }}>Loading menu…</p>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <div className="mn-root">
      <Navbar />

      <div className="mn-main">

        {/* ── Category Sidebar ── */}
        <aside className="mn-cat-sidebar">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`mn-cat-btn ${activeCat === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCat(cat.id)}
            >
              <span className="mn-cat-icon">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
          <div className="mn-cat-divider" />
          <button className="mn-cat-add" title="Add category" onClick={() => setShowCatModal(true)}>
            <Plus size={16} />
          </button>
        </aside>

        {/* ── Main Content ── */}
        <div className="mn-content">

          {/* Toolbar */}
          <div className="mn-toolbar">
            <div className="mn-toolbar-left">
              <h2 className="mn-page-title">
                {categories.find(c => c.id === activeCat)?.icon}{' '}
                {activeCat === 'all' ? 'Menu' : categories.find(c => c.id === activeCat)?.name || 'Menu'}
                {' '}<span>Items</span>
              </h2>
              <span className="mn-count-pill">{filtered.length} items</span>
              {stats.unavailable > 0 && (
                <span style={{ fontSize:11, fontWeight:700, color:'#b87200',
                  background:'rgba(255,159,28,0.10)', padding:'3px 10px', borderRadius:100,
                  border:'1px solid rgba(255,159,28,0.25)' }}>
                  {stats.unavailable} unavailable
                </span>
              )}
            </div>

            <div className="mn-toolbar-right">
              <div className="mn-search">
                <Search size={14} />
                <input
                  placeholder="Search items…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button style={{ background:'none', border:'none', cursor:'pointer',
                    color:'var(--muted)', display:'flex', alignItems:'center' }}
                    onClick={() => setSearch('')}>
                    <X size={13} />
                  </button>
                )}
              </div>

              <div className="mn-view-toggle">
                <button className={`mn-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}><LayoutGrid size={15} /></button>
                <button className={`mn-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}><List size={15} /></button>
              </div>

              <button className="mn-new-btn" onClick={openCreate}>
                <Plus size={16} /> New Item
              </button>
            </div>
          </div>

          {/* Sort bar */}
          <div className="mn-sort-bar">
            <Filter size={13} style={{ color:'var(--muted)', flexShrink:0 }} />
            {SORT_OPTIONS.map(s => (
              <button key={s.id}
                className={`mn-sort-chip ${sortBy === s.id ? 'active' : ''}`}
                onClick={() => setSortBy(s.id)}>
                {s.label}
              </button>
            ))}
            <div className="mn-sort-divider" />
            <button
              className={`mn-sort-chip ${search || activeCat !== 'all' ? 'active' : ''}`}
              onClick={() => { setSearch(''); setActiveCat('all'); setSortBy('default'); }}>
              <X size={10} /> Clear filters
            </button>
          </div>

          {/* Product Grid */}
          <div className="mn-grid-wrap">
            <div className={`mn-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
              {filtered.length === 0 ? (
                <div className="mn-empty">
                  <div className="mn-empty-icon">🍽️</div>
                  <p>No items found</p>
                  <span>Try adjusting your search or filters</span>
                  <button className="mn-new-btn" style={{ marginTop:8 }} onClick={openCreate}>
                    <Plus size={14} /> Add First Item
                  </button>
                </div>
              ) : (
                filtered.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isSelected={selectedItem?.id === product.id}
                    viewMode={viewMode}
                    onClick={() => openEdit(product)}
                    onEdit={e => { e.stopPropagation(); openEdit(product); }}
                    onDelete={e => { e.stopPropagation(); handleDelete(product); }}
                    onToggleAvail={e => handleToggleAvail(product, e)}
                    onDuplicate={e => { e.stopPropagation(); handleDuplicate(product); }}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════
            RIGHT PANEL
        ═══════════════════════════════════════ */}
        <aside className={`mn-panel ${panel ? 'open' : ''}`}>
          <div className="mn-panel-inner">

            {(panel === 'create' || panel === 'edit') && (
              <>
                <div className="mn-panel-header">
                  <div className="mn-panel-title">
                    {panel === 'create' ? 'New Menu Item' : `Edit · ${selectedItem?.name}`}
                  </div>
                  <button className="mn-panel-close" onClick={closePanel}><X size={15} /></button>
                </div>

                <div className="mn-panel-tabs">
                  {[{ id:'info', label:'Info' }, { id:'details', label:'Details' }].map(t => (
                    <button key={t.id}
                      className={`mn-panel-tab ${panelTab === t.id ? 'active' : ''}`}
                      onClick={() => setPanelTab(t.id)}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="mn-panel-body">
                  <div className="mn-form">

                    {/* ══════════════ INFO TAB ══════════════ */}
                    {panelTab === 'info' && (
                      <>
                        {/* ── Image Upload ── */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Item Image</label>

                          {/* Preview area */}
                          {form.image_preview ? (
                            <div style={{ position:'relative', borderRadius:'var(--r-md)',
                              overflow:'hidden', height:130, marginBottom:8 }}>
                              <img
                                src={form.image_preview}
                                alt="preview"
                                style={{ width:'100%', height:'100%', objectFit:'cover' }}
                                onError={clearImage}
                              />
                              {/* File name badge if file was picked */}
                              {form.image_file && (
                                <div style={{
                                  position:'absolute', bottom:0, left:0, right:0,
                                  background:'rgba(0,0,0,0.55)', padding:'4px 10px',
                                  fontSize:10, color:'#fff', fontWeight:600,
                                  display:'flex', alignItems:'center', gap:5
                                }}>
                                  <ImageIcon size={10} />
                                  {form.image_file.name}
                                  <span style={{ marginLeft:'auto', opacity:0.7 }}>
                                    {(form.image_file.size / 1024).toFixed(0)} KB
                                  </span>
                                </div>
                              )}
                              <button onClick={clearImage} style={{
                                position:'absolute', top:8, right:8,
                                width:26, height:26, borderRadius:8,
                                background:'rgba(26,15,10,0.65)', border:'none',
                                color:'white', cursor:'pointer',
                                display:'flex', alignItems:'center', justifyContent:'center'
                              }}>
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            /* Drop / click zone */
                            <div
                              className="mn-img-upload"
                              onClick={() => fileInputRef.current?.click()}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => {
                                e.preventDefault();
                                const file = e.dataTransfer.files[0];
                                if (file) {
                                  // reuse handler via synthetic event shape
                                  handleImageFileChange({ target: { files: [file] } });
                                }
                              }}
                              style={{ cursor:'pointer' }}
                            >
                              <div className="mn-img-upload-icon"><Upload size={20} /></div>
                              <p>
                                <strong>Click to browse</strong> or drag & drop<br />
                                <span style={{ fontSize:11, color:'var(--muted)' }}>
                                  JPG, PNG, WebP — max 5 MB
                                </span>
                              </p>
                            </div>
                          )}

                          {/* Hidden file input */}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            style={{ display:'none' }}
                            onChange={handleImageFileChange}
                          />

                          {/* OR paste URL */}
                          <div style={{ display:'flex', alignItems:'center', gap:6, margin:'6px 0 4px' }}>
                            <div style={{ flex:1, height:1, background:'var(--border)' }} />
                            <span style={{ fontSize:10, color:'var(--muted)', fontWeight:600 }}>OR PASTE URL</span>
                            <div style={{ flex:1, height:1, background:'var(--border)' }} />
                          </div>
                          <input
                            className="mn-form-input"
                            placeholder="https://example.com/food.jpg"
                            value={form.image_url}
                            onChange={e => handleImageUrlChange(e.target.value)}
                          />

                          {/* Upload progress bar */}
                          {saving && form.image_file && uploadProgress > 0 && uploadProgress < 100 && (
                            <div style={{ marginTop:6 }}>
                              <div style={{ display:'flex', justifyContent:'space-between',
                                fontSize:10, color:'var(--muted)', marginBottom:3 }}>
                                <span>Uploading image…</span>
                                <span>{uploadProgress}%</span>
                              </div>
                              <div style={{ height:4, background:'var(--border)', borderRadius:4 }}>
                                <div style={{
                                  height:'100%', borderRadius:4,
                                  background:'linear-gradient(90deg, var(--tomato), var(--saffron))',
                                  width: `${uploadProgress}%`,
                                  transition:'width 0.2s'
                                }} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Name */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Item Name *</label>
                          <input className="mn-form-input" placeholder="e.g. Butter Chicken"
                            value={form.name} onChange={e => setF('name', e.target.value)} />
                        </div>

                        {/* Category */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Category</label>
                       
<select className="mn-form-select"
  value={form.category} onChange={e => setF('category', e.target.value)}>
  <option value="">— Select Category —</option>
  {categories.filter(c => c.id !== 'all').length === 0 ? (
    <option disabled value="">No categories yet — add one first</option>
  ) : (
    categories.filter(c => c.id !== 'all').map(c => (
      <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
    ))
  )}
</select>
                        </div>

                        {/* Pricing */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Pricing</label>

                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)',
                              textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:5 }}>
                              Full Plate Price (₹) *
                            </div>
                            <input className="mn-form-input" type="number" step="0.01" min="0"
                              placeholder="e.g. 250.00"
                              value={form.price_full}
                              onChange={e => setF('price_full', e.target.value)} />
                          </div>

                          {/* Half plate toggle */}
                          <div style={{
                            display:'flex', alignItems:'center', gap:10,
                            background:'rgba(255,140,0,0.06)',
                            border:'1.5px dashed rgba(255,140,0,0.25)',
                            borderRadius:10, padding:'10px 14px', marginBottom:10
                          }}>
                            <label className="mn-toggle-switch" style={{ flexShrink:0 }}>
                              <input type="checkbox" checked={form.has_half_plate}
                                onChange={e => {
                                  setF('has_half_plate', e.target.checked);
                                  if (!e.target.checked) setF('price_half', '');
                                }} />
                              <span className="mn-toggle-track" />
                            </label>
                            <div>
                              <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>
                                Half Plate Available
                              </div>
                              <div style={{ fontSize:11, color:'var(--muted)', marginTop:1 }}>
                                Enable separate half plate pricing
                              </div>
                            </div>
                          </div>

                          {form.has_half_plate && (
                            <div style={{ background:'rgba(255,140,0,0.04)',
                              border:'1px solid rgba(255,140,0,0.18)', borderRadius:10, padding:'12px 14px' }}>
                              <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)',
                                textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:5 }}>
                                Half Plate Price (₹) *
                              </div>
                              <input className="mn-form-input" type="number" step="0.01" min="0"
                                placeholder="e.g. 140.00"
                                value={form.price_half}
                                onChange={e => setF('price_half', e.target.value)} />
                              {form.price_full && form.price_half && (
                                <div style={{ fontSize:11, color:'var(--muted)', marginTop:6, display:'flex', gap:12 }}>
                                  <span>Full: <strong style={{color:'var(--tomato)'}}>₹{parseFloat(form.price_full).toFixed(2)}</strong></span>
                                  <span>Half: <strong style={{color:'var(--saffron)'}}>₹{parseFloat(form.price_half).toFixed(2)}</strong></span>
                                  <span>Save: <strong style={{color:'var(--mint)'}}>
                                    ₹{(parseFloat(form.price_full) - parseFloat(form.price_half)).toFixed(2)}
                                  </strong></span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Description</label>
                          <textarea className="mn-form-textarea" placeholder="Describe this dish…"
                            value={form.description} onChange={e => setF('description', e.target.value)} />
                        </div>

                        {/* Availability */}
                        <div className="mn-avail-toggle">
                          <div className="mn-avail-toggle-left">
                            <span className="mn-avail-toggle-label">Available on menu</span>
                            <span className="mn-avail-toggle-sub">
                              {form.is_available ? 'Will be shown to customers' : 'Will be hidden'}
                            </span>
                          </div>
                          <label className="mn-toggle-switch">
                            <input type="checkbox" checked={form.is_available}
                              onChange={e => setF('is_available', e.target.checked)} />
                            <span className="mn-toggle-track" />
                          </label>
                        </div>
                      </>
                    )}

                    {/* ══════════════ DETAILS TAB ══════════════ */}
                    {panelTab === 'details' && (
                      <>
                        <div className="mn-form-row">
                          <div className="mn-form-group">
                            <label className="mn-form-label">Calories (kcal)</label>
                            <input className="mn-form-input" type="number" placeholder="850"
                              value={form.calories} onChange={e => setF('calories', e.target.value)} />
                          </div>
                          <div className="mn-form-group">
                            <label className="mn-form-label">Prep Time</label>
                            <input className="mn-form-input" placeholder="12m"
                              value={form.prep_time} onChange={e => setF('prep_time', e.target.value)} />
                          </div>
                        </div>

                        <div className="mn-form-group">
                          <label className="mn-form-label">Tags</label>
                          <div className="mn-tag-input-wrap"
                            onClick={() => tagInputRef.current?.focus()}>
                            {form.tags.map(t => (
                              <span key={t} className="mn-tag-chip">
                                {t}
                                <button onClick={() => removeTag(t)}><X size={9} /></button>
                              </span>
                            ))}
                            <input
                              ref={tagInputRef}
                              className="mn-tag-input"
                              placeholder="Type tag + Enter"
                              value={tagInput}
                              onChange={e => setTagInput(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
                                if (e.key === 'Backspace' && !tagInput && form.tags.length > 0)
                                  setF('tags', form.tags.slice(0, -1));
                              }}
                            />
                          </div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:4 }}>
                            {["Popular","New","Spicy","Vegan","Premium","Classic","Chef's Pick","Bestseller","Must Try"]
                              .filter(s => !form.tags.includes(s))
                              .map(s => (
                                <button key={s}
                                  style={{ fontSize:10, padding:'2px 8px', borderRadius:100,
                                    background:'rgba(255,77,46,0.06)', border:'1px dashed rgba(255,77,46,0.25)',
                                    color:'var(--tomato)', cursor:'pointer', fontWeight:700, fontFamily:'var(--font-body)' }}
                                  onClick={() => setF('tags', [...form.tags, s])}>
                                  + {s}
                                </button>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Save */}
                    <button className="mn-btn-save" onClick={handleSave} disabled={saving}>
                      {saving
                        ? <><Loader2 size={15} className="mn-spin" />
                            {form.image_file && uploadProgress < 100
                              ? `Uploading… ${uploadProgress}%`
                              : 'Saving…'}
                          </>
                        : <><CheckCircle2 size={15} />
                            {panel === 'create' ? 'Add to Menu' : 'Save Changes'}
                          </>}
                    </button>
                  </div>
                </div>
              </>
            )}

            {!panel && (
              <div style={{ flex:1, display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center', gap:12,
                color:'var(--light-muted)', textAlign:'center', padding:24 }}>
                <div style={{ fontSize:40 }}>🍽️</div>
                <p style={{ fontWeight:600, fontSize:13 }}>Click any item or "New Item" to get started</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Category Modal */}
      {showCatModal && (
        <div className="mn-modal-overlay" onClick={() => setShowCatModal(false)}>
          <div className="mn-modal" onClick={e => e.stopPropagation()}>
            <div className="mn-modal-header">
              <div className="mn-modal-title">New Category</div>
              <button className="mn-modal-close" onClick={() => setShowCatModal(false)}><X size={14} /></button>
            </div>
            <div className="mn-modal-body">
              <div className="mn-form-group">
                <label className="mn-form-label">Icon (emoji)</label>
                <input className="mn-form-input" placeholder="🍕"
                  value={newCatIcon} onChange={e => setNewCatIcon(e.target.value)}
                  style={{ fontSize:22, textAlign:'center', letterSpacing:4 }} />
              </div>
              <div className="mn-form-group">
                <label className="mn-form-label">Category Name</label>
                <input className="mn-form-input" placeholder="e.g. Starters"
                  value={newCatName} onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCategory()} />
              </div>
              <div className="mn-form-group">
                <label className="mn-form-label">Or pick a preset</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:180, overflowY:'auto' }}>
                  {PRESET_CATEGORIES.map(c => (
                    <button key={c.id}
                      onClick={() => { setNewCatName(c.name); setNewCatIcon(c.icon); }}
                      style={{ fontSize:11, padding:'4px 10px', borderRadius:100,
                        background: newCatName === c.name ? 'rgba(255,77,46,0.12)' : 'var(--surface)',
                        border: newCatName === c.name ? '1.5px solid var(--tomato)' : '1px solid var(--border)',
                        color:'var(--text)', cursor:'pointer', fontWeight:600,
                        display:'flex', alignItems:'center', gap:4 }}>
                      {c.icon} {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <button className="mn-btn-save" onClick={handleAddCategory}>
                <Plus size={15} /> Create Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <div className={`mn-toast ${toast.show ? 'show' : ''}`}>
        <CheckCircle2 size={14} className="mn-toast-icon" />
        {toast.msg}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   PRODUCT CARD
═══════════════════════════════════════ */
function ProductCard({ product, isSelected, viewMode, onClick, onEdit, onDelete, onToggleAvail, onDuplicate }) {
const imgSrc = `${imgurl}${product.image_url || product.image_url}`;
  const catEmoji  = PRESET_CATEGORIES.find(c => c.id === product.category)?.icon || '🍽️';
  const fullPrice = Number(product.price_full ?? product.price ?? 0).toFixed(2);
  const halfPrice = product.has_half_plate && product.price_half
    ? Number(product.price_half).toFixed(2) : null;


    console.log("product", product);

  if (viewMode === 'list') {
    return (
      <div className={`mn-card list-view-card ${isSelected ? 'selected' : ''} ${!product.is_available ? 'unavailable' : ''}`}
        onClick={onClick}>
        <div className="mn-card-img">
          {imgSrc ? <img src={imgSrc} alt={product.name} /> : <div className="mn-card-img-placeholder">{catEmoji}</div>}
          <span className={`mn-avail-dot ${product.is_available ? 'on' : 'off'}`} />
        </div>
        <div className="mn-card-body">
          <div className="mn-card-top">
            <div className="mn-card-name" style={{ fontSize:14 }}>{product.name}</div>
            {product.tags?.length > 0 && (
              <span className={`mn-badge ${product.tags[0].toLowerCase().replace(/[^a-z]/g,'')}`}>{product.tags[0]}</span>
            )}
          </div>
          <div className="mn-card-meta" style={{ borderTop:'none', paddingTop:0, gap:8 }}>
            <span className="mn-card-kcal"><Flame size={10} /> {product.calories || '—'}</span>
            <span className="mn-card-kcal"><Clock size={10} /> {product.prep_time || product.prep || '—'}</span>
          </div>
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div className="mn-card-price">₹{fullPrice}</div>
            {halfPrice && <div style={{ fontSize:10, color:'var(--muted)', fontWeight:600 }}>Half ₹{halfPrice}</div>}
          </div>
          <div className="mn-card-actions">
            <button className="mn-card-action-btn" onClick={onEdit}><Edit3 size={12} /></button>
            <button className="mn-card-action-btn" onClick={onDuplicate}><Copy size={12} /></button>
            <button className="mn-card-action-btn" onClick={onToggleAvail}>
              {product.is_available ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
            <button className="mn-card-action-btn danger" onClick={onDelete}><Trash2 size={12} /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mn-card ${isSelected ? 'selected' : ''} ${!product.is_available ? 'unavailable' : ''}`}
      onClick={onClick}>
      <div className="mn-card-img">
        {imgSrc ? <img src={imgSrc} alt={product.name} /> : <div className="mn-card-img-placeholder">{catEmoji}</div>}
        {product.tags?.length > 0 && (
          <div className="mn-img-badges">
            {product.tags.slice(0, 2).map(t => (
              <span key={t} className={`mn-badge ${t.toLowerCase().replace(/[^a-z]/g,'')}`}>{t}</span>
            ))}
          </div>
        )}
        <span className={`mn-avail-dot ${product.is_available ? 'on' : 'off'}`} />
        <div className="mn-prep-badge"><Clock size={9} /> {product.prep_time || product.prep || '—'}</div>
        {product.has_half_plate && (
          <div style={{ position:'absolute', top:8, left:8,
            background:'rgba(255,140,0,0.88)', backdropFilter:'blur(4px)',
            color:'#fff', fontSize:9, fontWeight:800,
            padding:'2px 7px', borderRadius:100, letterSpacing:'0.5px', textTransform:'uppercase' }}>
            ½ Avail.
          </div>
        )}
      </div>
      <div className="mn-card-body">
        <div className="mn-card-top">
          <div className="mn-card-name">{product.name}</div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div className="mn-card-price">₹{fullPrice}</div>
            {halfPrice && <div style={{ fontSize:10, color:'var(--saffron)', fontWeight:700, marginTop:1 }}>½ ₹{halfPrice}</div>}
          </div>
        </div>
        <p className="mn-card-desc">{product.description || product.desc || 'No description'}</p>
        <div className="mn-card-meta">
          <span className="mn-card-kcal">
            <Flame size={11} style={{ color:'var(--saffron)' }} />
            {product.calories || '—'} kcal
          </span>
          <div className="mn-card-actions" onClick={e => e.stopPropagation()}>
            <button className="mn-card-action-btn" onClick={onEdit}><Edit3 size={12} /></button>
            <button className="mn-card-action-btn" onClick={onDuplicate}><Copy size={12} /></button>
            <button className="mn-card-action-btn" onClick={onToggleAvail}>
              {product.is_available ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
            <button className="mn-card-action-btn danger" onClick={onDelete}><Trash2 size={12} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Menus;
